import { Component, computed, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { map } from 'rxjs/operators';
import {
    LucideBanknote,
    LucideBell,
    LucideBellOff,
    LucideCheck,
    LucideCopy,
    LucideIcon,
    LucideTriangleAlert,
    LucideUserPlus,
    LucideUsers,
} from '@lucide/angular';
import { OnClickOutsideDirective } from '@knalgeel/pandora';
import { MessageService } from 'primeng/api';
import {
    MarkAllNotificationsReadGQL,
    MarkNotificationReadGQL,
    Notification,
    NotificationsGQL,
    NotificationType,
} from '../../../../graphql/generated';
import { TimeAgoPipe } from '../../pipes/time-ago.pipe';
import { AppContext } from '../../core/services/app-context.service';
import { IconComponent } from '../../shared/components/icon/icon.component';

type NotificationItem = Pick<
    Notification,
    | 'id'
    | 'type'
    | 'title'
    | 'message'
    | 'relatedEntityId'
    | 'relatedEntityType'
    | 'establishmentId'
    | 'isRead'
    | 'createdAt'
>;

@Component({
    selector: 'app-notification-bell',
    templateUrl: './notification-bell.component.html',
    imports: [IconComponent, OnClickOutsideDirective, TimeAgoPipe, RouterLink],
})
export class NotificationBellComponent implements OnDestroy {

    protected readonly bellIcon = LucideBell;
    protected readonly bellOffIcon = LucideBellOff;
    protected readonly checkIcon = LucideCheck;

    // Poll every 60s so the bell stays fresh without manual refresh.
    private readonly notificationsRef = this.notificationsGQL.watch(undefined, {
        pollInterval: 60_000,
        fetchPolicy: 'cache-and-network',
    });

    protected readonly open = signal(false);

    // The backend only ever returns the current user's *unread* notifications.
    protected readonly notifications = toSignal(
        this.notificationsRef.valueChanges.pipe(
            map(result => (result.data?.notifications ?? []) as NotificationItem[]),
        ),
        { initialValue: [] as NotificationItem[] },
    );

    // Filter on isRead rather than total length: the backend currently returns only unread, but if
    // that ever changes the badge and the markAllAsRead early-return must still reflect unread only.
    protected readonly unreadCount = computed(() => this.notifications().filter(n => !n.isRead).length);

    constructor(
        private readonly notificationsGQL: NotificationsGQL,
        private readonly markNotificationReadGQL: MarkNotificationReadGQL,
        private readonly markAllNotificationsReadGQL: MarkAllNotificationsReadGQL,
        private readonly router: Router,
        private readonly messageService: MessageService,
        private readonly appContext: AppContext,
    ) {
    }

    // Stop the 60s Apollo poll timer when the bell is destroyed (e.g. on logout); otherwise the
    // underlying ObservableQuery keeps polling and a fresh poller is added on every re-login.
    public ngOnDestroy(): void {
        this.notificationsRef.stopPolling();
    }

    protected toggle(): void {
        this.open.update(value => !value);
    }

    // Passed to [pandoraOnClickOutside]; arrow keeps `this` bound without re-binding each render.
    protected readonly closePanel = (): void => this.open.set(false);

    protected onNotificationClick(notification: NotificationItem): void {
        this.open.set(false);

        const target = this.routerLinkFor(notification);
        if (!target) {
            // Nothing to navigate to, so consume the notification immediately.
            this.markAsRead(notification.id);
            return;
        }

        // The related entity (an employee) is establishment-scoped, so switch into the
        // establishment it belongs to before navigating — otherwise the deep link dead-ends
        // on the empty state or resolves nothing under the wrong establishment.
        if (notification.establishmentId) {
            this.appContext.set('establishmentId', notification.establishmentId);
        }

        // Only mark as read once navigation actually succeeds: if a guard rejects the route
        // (e.g. an expired session redirecting to login), the notification stays unread.
        this.router.navigate(target).then(navigated => {
            if (navigated) {
                this.markAsRead(notification.id);
            }
        });
    }

    protected markAllAsRead(): void {
        if (this.unreadCount() === 0) {
            return;
        }

        this.markAllNotificationsReadGQL
            .mutate({}, { refetchQueries: [this.notificationsGQL.document] })
            .subscribe({ error: () => this.showMutationError() });
    }

    /** Picks an icon per notification type so the kinds are visually distinct in the list. */
    protected iconFor(type: NotificationType): LucideIcon {
        switch (type) {
            case NotificationType.PossibleDuplicateEmployee:
                return LucideUsers;
            case NotificationType.LowAfboekingen:
                return LucideTriangleAlert;
            case NotificationType.UnknownEstablishmentEmployee:
                return LucideUserPlus;
            case NotificationType.PossibleDuplicateOpportunity:
                return LucideCopy;
            case NotificationType.SaleAssistClaimed:
                return LucideBanknote;
            default:
                return LucideBell;
        }
    }

    private markAsRead(notificationId: string): void {
        this.markNotificationReadGQL
            .mutate(
                { input: { notificationId } },
                { refetchQueries: [this.notificationsGQL.document] },
            )
            .subscribe({ error: () => this.showMutationError() });
    }

    private showMutationError(): void {
        this.messageService.add({
            severity: 'error',
            summary: 'Mislukt',
            detail: 'De melding kon niet worden bijgewerkt. Probeer het opnieuw.',
        });
    }

    /** Maps a notification's related entity to an in-app route, or null when it doesn't link anywhere. */
    private routerLinkFor(notification: NotificationItem): unknown[] | null {
        if (notification.relatedEntityType === 'Employee' && notification.relatedEntityId) {
            return ['/establishment', 'employees', notification.relatedEntityId];
        }

        if (notification.relatedEntityType === 'Opportunity' && notification.relatedEntityId) {
            return ['/establishment', 'sales', 'opportunities', notification.relatedEntityId];
        }

        return null;
    }
}
